import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Brand } from "@/components/bits";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

// Error copy for the backend's reason field on /auth/login.
const REASONS = {
  unverified: {
    title: "Verify your email",
    sub: "Enter the 6-digit code we emailed you to finish setting up your account.",
  },
  pending: {
    title: "Waiting for AgriVoice to activate the account",
    sub: "Contact an AgriVoice admin at agrivoice.co.in after signup. They will activate the account.",
  },
  disabled: {
    title: "Account disabled",
    sub: "This account has been disabled. Contact your organisation admin.",
  },
};

export default function Login() {
  const { loginWithPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // { reason, title, sub }
  const [code, setCode] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resent, setResent] = useState(false);
  const [devCode, setDevCode] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginWithPassword(email.trim(), password);
      window.location.href = "/dashboard";
    } catch (err) {
      const d = err.response?.data || {};
      const known = REASONS[d.reason];
      setError(
        known
          ? { reason: d.reason, title: known.title, sub: d.detail || known.sub }
          : { reason: null, title: "Sign-in failed", sub: d.detail || "Check your email and password and try again." }
      );
      setBusy(false);
    }
  };

  const resend = async () => {
    setResendBusy(true);
    try {
      const { data } = await api.post("/auth/resend-code", { email: email.trim() });
      setResent(true);
      if (data?.dev_code) setDevCode(data.dev_code);
    } catch (err) {
      setError({
        reason: "unverified",
        title: "Verify your email",
        sub: err.response?.data?.detail || "Could not resend the code — try again in a minute.",
      });
    } finally {
      setResendBusy(false);
    }
  };

  const verify = async () => {
    setVerifyBusy(true);
    try {
      const { data } = await api.post("/auth/verify", { email: email.trim(), code: code.trim() });
      if (data?.status === "active" && data.token) {
        try {
          localStorage.setItem("av_token", data.token);
        } catch (err) {
          /* localStorage unavailable — cookie session still applies */
        }
        window.location.href = "/dashboard";
        return;
      }
      if (data?.status === "pending_approval") {
        setError({ reason: "pending", title: REASONS.pending.title, sub: data.detail || REASONS.pending.sub });
      } else {
        setError({ reason: "unverified", title: "Verify your email", sub: "That code did not work — check it or resend a new one." });
      }
    } catch (err) {
      setError({
        reason: "unverified",
        title: "Verify your email",
        sub: err.response?.data?.detail || "Invalid or expired code — try again or resend a new one.",
      });
    } finally {
      setVerifyBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#080C0A] grain flex flex-col">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-block text-[11px] font-mono text-slate-500 hover:text-emerald-300 mb-6" data-testid="login-back-home">← Back to home</Link>

          <div className="glass p-7 sm:p-8 rise" data-testid="login-card">
            <div className="flex items-center gap-3 mb-7">
              <Brand size="lg" />
            </div>

            <h1 className="font-display text-2xl font-bold text-slate-50">Welcome back</h1>
            <p className="text-sm text-slate-400 mt-1.5">Sign in with your business email to manage your agents.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              <div>
                <label className="field-label" htmlFor="login-email">Business email</label>
                <input id="login-email" type="email" autoComplete="email" required className="field" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              </div>
              <div>
                <label className="field-label" htmlFor="login-password">Password</label>
                <div className="relative">
                  <input id="login-password" type={showPw ? "text" : "password"} autoComplete="current-password" required className="field pr-14" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-slate-500 hover:text-slate-200" data-testid="login-show-password">{showPw ? "hide" : "show"}</button>
                </div>
              </div>

              {error && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-950/40 px-3.5 py-3 text-xs" data-testid="login-error" data-reason={error.reason || "error"}>
                  <p className="font-semibold text-rose-200">{error.title}</p>
                  <p className="mt-1 leading-relaxed text-rose-300/90">{error.sub}</p>
                  {error.reason === "unverified" && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" className="field flex-1 font-mono tracking-[0.3em]" data-testid="login-code-input" />
                        <button type="button" onClick={verify} disabled={verifyBusy || code.length !== 6} className="btn-primary btn-sm shrink-0" data-testid="login-verify-btn">
                          {verifyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Verify"}
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <button type="button" onClick={resend} disabled={resendBusy || resent} className="text-[11px] font-mono text-emerald-300 hover:text-emerald-200 disabled:text-slate-500" data-testid="login-resend-btn">
                          {resendBusy ? "sending…" : resent ? "code sent ✓" : "Resend code"}
                        </button>
                        {devCode && (
                          <button type="button" onClick={() => setCode(devCode)} className="text-[11px] font-mono text-amber-300 hover:text-amber-200" data-testid="login-dev-code" title="Dev mode: fill the code">dev code · {devCode}</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="login-submit">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

          </div>

          <p className="text-center text-sm text-slate-500 mt-5">
            New to AgriVoice? <Link to="/signup" className="text-emerald-300 hover:text-emerald-200" data-testid="login-signup-link">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
